import { useState } from 'react';
import { Button } from '@components/common/button';
import { LoadingSpinner } from '@components/common/loading_spinner';
import {
  ArrowLeft,
  Calendar,
  Droplets,
  TrendingUp,
  TrendingDown,
  Image as ImageIcon,
  FileText,
  Download,
  BarChart3,
  Activity
} from 'lucide-react';
import { Meter, MeterReading, MeterReadingStats } from '@/types/meter_types';
import { Modal } from '@components/common/modal';

interface MeterHistoryProps {
  meter: Meter | null;
  readings: MeterReading[];
  stats?: MeterReadingStats;
  is_loading?: boolean;
  on_export?: () => void;
  on_back?: () => void;
}

export const MeterHistory: React.FC<MeterHistoryProps> = ({
  meter,
  readings,
  stats,
  is_loading = false,
  on_export,
  on_back
}) => {
  const [selected_reading, set_selected_reading] = useState<MeterReading | null>(null);
  const [show_photo_modal, set_show_photo_modal] = useState(false);

  // Helper to safely convert to number
  const to_number = (value: any): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return parseFloat(value) || 0;
    return 0;
  };

  if (is_loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading meter history..." />
      </div>
    );
  }

  if (!meter) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <Droplets size={48} className="mx-auto text-medium-gray mb-4" />
          <p className="text-medium-gray">Meter not found</p>
          <Button variant="primary" onClick={on_back} className="mt-4">
            Back to Meters
          </Button>
        </div>
      </div>
    );
  }

  const get_consumption_trend = (current: MeterReading, index: number) => {
    if (index === readings.length - 1) return null;
    const previous = readings[index + 1];
    if (!previous || !current.consumption || !previous.consumption) return null;

    const current_consumption = to_number(current.consumption);
    const previous_consumption = to_number(previous.consumption);
    
    if (previous_consumption === 0) return null;

    const difference = current_consumption - previous_consumption;
    const percentage = ((difference / previous_consumption) * 100).toFixed(1);

    return {
      difference,
      percentage: parseFloat(percentage),
      is_increase: difference > 0
    };
  };

  const format_date = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handle_view_photo = (reading: MeterReading) => {
    set_selected_reading(reading);
    set_show_photo_modal(true);
  };

  return (
    <>
      <div className="min-h-screen">
        {/* Header */}
        <div className="bg-white/95 border-b border-light-gray sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={on_back}
                  icon_left={<ArrowLeft size={18} />}
                >
                  Back
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-charcoal">Meter Reading History</h1>
                  <p className="text-sm text-medium-gray mt-0.5">
                    {meter.meter_number}
                  </p>
                </div>
              </div>

              {on_export && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={on_export}
                  icon_left={<Download size={18} />}
                >
                  Export History
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="space-y-6">
            {/* Meter Header */}
            <div className="bg-gradient-to-r from-forest-green to-dark-forest rounded-md p-6 text-white border border-light-gray">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-md bg-lime-green text-forest-green flex items-center justify-center flex-shrink-0">
                  <Droplets size={28} />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-1">{meter.meter_number}</h2>
                  <p className="text-sm opacity-90">{meter.client_name}</p>
                  {meter.location && (
                    <p className="text-xs opacity-75 mt-1">{meter.location}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Statistics Summary */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white p-5 rounded-md border border-light-gray">
                  <div className="flex items-center gap-2 text-medium-gray mb-2">
                    <Activity size={18} />
                    <p className="text-sm font-medium">Total Readings</p>
                  </div>
                  <p className="text-2xl font-bold text-charcoal">
                    {to_number(stats.total_readings)}
                  </p>
                </div>

                <div className="bg-white p-5 rounded-md border border-light-gray">
                  <div className="flex items-center gap-2 text-medium-gray mb-2">
                    <Droplets size={18} />
                    <p className="text-sm font-medium">Total Consumption</p>
                  </div>
                  <p className="text-2xl font-bold text-forest-green">
                    {to_number(stats.total_consumption).toLocaleString()} units
                  </p>
                </div>

                <div className="bg-white p-5 rounded-md border border-light-gray">
                  <div className="flex items-center gap-2 text-medium-gray mb-2">
                    <TrendingUp size={18} />
                    <p className="text-sm font-medium">Avg Consumption</p>
                  </div>
                  <p className="text-2xl font-bold text-charcoal">
                    {to_number(stats.average_consumption).toFixed(1)} units
                  </p>
                </div>

                <div className="bg-white p-5 rounded-md border border-light-gray">
                  <div className="flex items-center gap-2 text-medium-gray mb-2">
                    <BarChart3 size={18} />
                    <p className="text-sm font-medium">Peak Consumption</p>
                  </div>
                  <p className="text-2xl font-bold text-warning">
                    {to_number((stats as any).max_consumption).toFixed(1)} units
                  </p>
                </div>
              </div>
            )}

            {/* Reading History Timeline */}
            <div className="bg-white rounded-md border border-light-gray">
              <div className="p-6 border-b border-light-gray">
                <h3 className="text-lg font-bold text-charcoal">Reading Timeline</h3>
              </div>

              <div className="p-6">
                {readings.length === 0 ? (
                  <div className="text-center py-16">
                    <Droplets size={56} className="mx-auto text-medium-gray mb-4" />
                    <p className="text-medium-gray text-lg mb-2">No readings recorded yet</p>
                    <p className="text-sm text-medium-gray">
                      Start by recording the first meter reading
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {readings.map((reading, index) => {
                      const trend = get_consumption_trend(reading, index);
                      const reading_value = to_number(reading.current_reading);
                      const consumption = to_number(reading.consumption);

                      return (
                        <div
                          key={reading.id}
                          className="bg-off-white border border-light-gray rounded-md p-5 hover:border-forest-green transition-colors"
                        >
                          <div className="flex items-start justify-between gap-4">
                            {/* Left: Reading Info */}
                            <div className="flex-1 space-y-3">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-md bg-forest-green bg-opacity-10 text-forest-green flex items-center justify-center font-bold text-lg flex-shrink-0">
                                  #{readings.length - index}
                                </div>
                                <div>
                                  <p className="font-bold text-charcoal text-lg">
                                    {reading_value.toLocaleString()} units
                                  </p>
                                  <p className="text-sm text-medium-gray flex items-center gap-1.5">
                                    <Calendar size={14} />
                                    {format_date(reading.reading_date)}
                                  </p>
                                </div>
                              </div>

                              {consumption > 0 && (
                                <div className="flex items-center gap-3 pl-15">
                                  <div className="px-4 py-2 bg-lime-green bg-opacity-20 rounded-md border border-lime-green border-opacity-30">
                                    <p className="text-sm font-semibold text-forest-green">
                                      Consumption: {consumption.toLocaleString()} units
                                    </p>
                                  </div>

                                  {trend && (
                                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold ${
                                      trend.is_increase 
                                        ? 'bg-warning bg-opacity-10 text-warning border border-warning'
                                        : 'bg-success bg-opacity-10 text-success border border-success'
                                    }`}>
                                      {trend.is_increase ? (
                                        <TrendingUp size={14} />
                                      ) : (
                                        <TrendingDown size={14} />
                                      )}
                                      {trend.is_increase ? '+' : ''}{trend.percentage}%
                                    </div>
                                  )}
                                </div>
                              )}

                              {reading.notes && (
                                <div className="pl-15 pt-3 border-t border-light-gray">
                                  <p className="text-sm text-medium-gray flex items-start gap-2">
                                    <FileText size={16} className="text-forest-green mt-0.5 flex-shrink-0" />
                                    <span className="italic">{reading.notes}</span>
                                  </p>
                                </div>
                              )}

                              {reading.reader_name && (
                                <p className="text-sm text-medium-gray pl-15">
                                  Recorded by: <span className="font-semibold text-charcoal">{reading.reader_name}</span>
                                </p>
                              )}
                            </div>

                            {/* Right: Photo */}
                            {reading.photo && (
                              <button
                                onClick={() => handle_view_photo(reading)}
                                className="relative flex-shrink-0 w-24 h-24 rounded-md overflow-hidden border-2 border-light-gray hover:border-forest-green transition-colors group"
                              >
                                <img
                                  src={reading.photo}
                                  alt="Meter reading"
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                                  <ImageIcon size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Modal */}
      {selected_reading && show_photo_modal && (
        <Modal
          is_open={show_photo_modal}
          on_close={() => set_show_photo_modal(false)}
          title="Meter Reading Photo"
          size="lg"
          footer={
            <Button variant="secondary" onClick={() => set_show_photo_modal(false)}>
              Close
            </Button>
          }
        >
          <div className="space-y-4">
            <div className="bg-off-white rounded-md overflow-hidden border border-light-gray">
              <img
                src={selected_reading.photo}
                alt="Meter reading"
                className="w-full h-auto"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-off-white p-4 rounded-md border border-light-gray">
                <p className="text-sm text-medium-gray mb-1">Reading Value</p>
                <p className="text-xl font-bold text-charcoal">
                  {to_number(selected_reading.current_reading).toLocaleString()} units
                </p>
              </div>
              <div className="bg-off-white p-4 rounded-md border border-light-gray">
                <p className="text-sm text-medium-gray mb-1">Date</p>
                <p className="text-xl font-bold text-charcoal">
                  {format_date(selected_reading.reading_date)}
                </p>
              </div>
            </div>

            {selected_reading.notes && (
              <div className="p-4 bg-info bg-opacity-5 rounded-md border border-info">
                <p className="text-sm font-semibold text-charcoal mb-2">Notes</p>
                <p className="text-sm text-medium-gray">{selected_reading.notes}</p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};